import { all, takeEvery, put, call, takeLatest } from 'redux-saga/effects';
import moment from "moment/moment";
import omit from 'lodash/omit';
import clone from 'clone';
import { newInvoice } from '../../pages/Invoice/config';
import actions from './actions';
import actionSerie from '@iso/redux/serie/actions';
import actionsLog from '@iso/redux/log/actions';
import { 
  simpleQueryFirestoreWithFilter, 
  simpleQueryFirestoreWithFilterOrderBy,
  paginatedQueryFirestoreWithFilter
} from '../../components/utility/firestore.helper';
import { rsf } from '@iso/lib/firebase/firebase';
import { noteSeries } from "@iso/config/site.config";

import { getAccountIdPrefix } from '@iso/components/library/helpers/local_storage';
import DateFormatter from "@iso/components/utility/DateFormatter";

import {updateValues} from "../../pages/Invoice/components/helpers";
import {generateSystemId} from "../../components/library/helpers/idGenerator";

const COLLECTION_NAME = 'invoices';
const COLLECTION_NAME_PROP = 'properties';
const COLLECTION_NAME_CLIENT = 'users';

export function* getInvoice(query) {
  try {
    const filterInvoice = { 'invoice':  {'value':  query.payload.id, 'operator': '==', 'field': 'id' }};
    const data = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME, filterInvoice);
    const dataSource = [];
    Object.keys(data).map((item, index) => {
      return dataSource.push({
        ...data[item],
        key: item,
      });
    });
    yield put(actions.getInvoiceSuccess(dataSource[0]));
  } catch (error) {
    yield put(actions.getInvoiceError(error));
  }
}

// Helper function to build filters from payload
function buildInvoiceFilters(filter) {
  let filters = [];
  if (filter !== undefined) {
    if (filter.type) {
      filters.push({ field: 'type', operator: '==', value: filter.type });
    }
    if (filter.startDate) {
      filters.push({ field: 'orderDate', operator: '>=', value: filter.startDate.valueOf() });
    }
    if (filter.endDate) {
      filters.push({ field: 'orderDate', operator: '<=', value: filter.endDate.valueOf() });
    }
    if (filter.billToId) {
      filters.push({ field: 'billToId', operator: '==', value: filter.billToId });
    }
    if (filter.idOwner) {
      filters.push({ field: 'idOwner', operator: '==', value: filter.idOwner });
    }
    if (filter.invoice_serie) {
      filters.push({ field: 'invoice_serie', operator: '==', value: filter.invoice_serie });
    }
    if (filter.orderStatus) {
      filters.push({ field: 'orderStatus', operator: '==', value: filter.orderStatus });
    }
  }
  filters.push({ field: 'deleted_at', operator: '==', value: null });
  return filters;
}

export function* getInvoices({ payload }) {
  try {
    const {filter} = payload;
    let filters = buildInvoiceFilters(filter);
    let orderBy = { field: "orderDate", type: "desc" };
    let data = yield simpleQueryFirestoreWithFilterOrderBy(getAccountIdPrefix() + COLLECTION_NAME, filters, orderBy);
    const dataSource = [];
    Object.keys(data).map((item, index) => {
      return dataSource.push({
        ...data[item],
        key: item,
      });
    });
    yield put({
      type: actions.GET_INVOICES_SUCCESS,
      invoices: dataSource
    });
  } catch (error) {
    console.log(error);
  }
}

/**
 * Paginated invoices fetch - loads only pageSize items at a time
 * Uses cursor-based pagination with cached cursors for efficient navigation
 */
export function* getInvoicesPaginated({ payload }) {
  try {
    const { filter, page = 1, pageSize = 50, lastDoc = null } = payload;
    let filters = buildInvoiceFilters(filter);
    let orderBy = { field: "orderDate", type: "desc" };
    
    // Get paginated data using cursor (no count query for performance)
    const result = yield paginatedQueryFirestoreWithFilter(
      getAccountIdPrefix() + COLLECTION_NAME,
      filters,
      orderBy,
      pageSize,
      lastDoc // Cursor from previous page
    );
    
    const dataSource = [];
    Object.keys(result.data).map((item, index) => {
      return dataSource.push({
        ...result.data[item],
        key: item,
      });
    });
    
    yield put({
      type: actions.GET_INVOICES_PAGINATED_SUCCESS,
      invoices: dataSource,
      hasMore: result.hasMore,
      currentPage: page,
      lastDoc: result.lastDoc,
      firstDoc: result.firstDoc
    });
  } catch (error) {
    console.log(error);
  }
}

export function* updateInvoiceSaga({ payload }) {
  const {invoice, actionName, filter } = payload;
  try {
    switch (actionName) {
      case 'delete': {
        // BR1: A DRAFT invoice with booking-linked items cannot be deleted manually.
        // Finalized invoices (status 'invoice' or 'note') can always be deleted —
        // the admin may need to void them to correct billing mistakes.
        // For drafts: the booking is the source of truth; items are removed via
        // the UPDATE_INVOICE_ITEM_BOOKING flow when the booking is modified/deleted.
        const hasBookingItems = !!(invoice.invoiceList &&
          invoice.invoiceList.some(item => item.bookingId));
        if (hasBookingItems && invoice.orderStatus === 'draft') {
          yield put({
            type: actions.DELETE_INVOICE_ERROR,
            payload: { message: 'This invoice has items linked to a booking and cannot be deleted. Remove or modify the booking items first.' },
          });
          return;
        }
        yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoice.key}`, {
          ...omit(invoice, ['key']), //soft deletion
          deleted_at: new Date().getTime(),
        });
        // NOTE: We do NOT re-dispatch UPDATE_INVOICE_ITEM_BOOKING here.
        // The booking items remain unchanged in the booking document.
        // Draft items: next booking save will automatically re-create the draft.
        // Finalized items: stay locked — manual deletion of a finalized invoice
        //   does not automatically re-bill. Admin must re-save the booking if needed.
        let filters = [{ field: 'invoice', operator: '==', value : invoice.id }];
        let data = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + 'payments', filters);
        if (data && Object.keys(data).length > 0){
          let paymentId = Object.keys(data);
          yield call(rsf.firestore.setDocument, getAccountIdPrefix() + `payments/${paymentId[0]}`, {
            ...data[paymentId[0]],
            deleted_at: new Date().getTime(),
          });
        }
        yield put({
          type: actions.DELETE_INVOICE_SUCCESS,
          payload: { message: '1 ' + invoice.type + ' deleted' },
        });
        break;
      }
      case 'update':
        yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoice.key}`, {
          ...omit(invoice, ['key']),
        });
        break;
      default:
        yield call(rsf.firestore.addDocument, getAccountIdPrefix() + COLLECTION_NAME, invoice);
        break;
    }
    yield put({ type: actions.UPDATE_INVOICE_SUCCESS });
    // Refresh with pagination after invoice update
    yield put({ type: actions.GET_INVOICES_PAGINATED, payload: { filter, page: 1, pageSize: 50 }});
  } catch (error) {
    console.log(error);
  }
}

export function* fixPaymentsInvoicesBulk_({payload}) {
  const {invoices} = payload;
  for (let y = 0; invoices.length > y; y++){
    //if (invoices[y].orderStatus === 'draft') continue;
    const previousTotal = invoices[y].totalCost;
    const updatedInvoice = updateValues(invoices[y]);
    yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoices[y].key}`, {
      ...omit(updatedInvoice, ['key'])
    });
    if (previousTotal !== updatedInvoice.totalCost) console.log('-- Fixed total '+previousTotal+' to '+updatedInvoice.totalCost);
    yield setPaymentInvoice(updatedInvoice);
    console.log(y+' fixed '+updatedInvoice.id);
  }
  console.log('termino');

}

export function* setPaymentInvoice(invoice) {
  if (invoice.invoice_serie === '7' || invoice.invoice_serie === '8') return; //fix to exclude rentals invoices from payments
  let filters = [{field: 'invoice', operator: '==', value : invoice.id}];
  let data = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + 'payments', filters);
  if (Object.keys(data).length > 0 ){
    let paymentId = Object.keys(data);
    yield call(rsf.firestore.setDocument, getAccountIdPrefix() + `payments/${paymentId[0]}`, {
      ...omit(data[paymentId[0]], ['description']),
      debit: invoice.totalCost,
      type:invoice.type,
      description: invoice.type +" #" + invoice.number,
    });
  }else{
    const payment = {
      credit: "0.00",
      debit: invoice.totalCost,
      date: invoice.orderDate,
      deleted_at: null,
      description: invoice.type + " #" + invoice.number,
      invoice: invoice.id,
      type: invoice.type,
      billToId: invoice.billToId,
      idOwner: invoice.billToId,//fix replaced idOwner
      createdAt: new Date().getTime(),
      id: 1,
    }
    yield call(rsf.firestore.addDocument, getAccountIdPrefix() + `payments`, payment);
  }
}

export function* updateInvoicesBulk({ payload }) {
  const getYearFromTimestamp = (d) => {
    let date = new Date(d);
    return date.getFullYear().toString();
  }
  const { invoices, series, filter } = payload;
  try {
    // Sort ASC by orderDate so earlier invoices get lower sequential numbers
    const sortedInvoices = [...invoices].sort((a, b) => a.orderDate - b.orderDate);
    let objectSeries = Object.keys(series);
    for (let i = 0; objectSeries.length > i ; i++){
      for (let y = 0; sortedInvoices.length > y; y++){
        if (!sortedInvoices[y].invoice_serie || sortedInvoices[y].invoice_serie === "0")
          sortedInvoices[y].invoice_serie = (sortedInvoices[y].type === "invoice") ? "1" : "101";
        if (String(series[objectSeries[i]].id) === sortedInvoices[y].invoice_serie && sortedInvoices[y].orderStatus === 'draft' ){
          let found = false;
          let year = getYearFromTimestamp(sortedInvoices[y].orderDate);
          if (series[objectSeries[i]].years !== undefined) {
            Object.keys(series[objectSeries[i]].years).forEach((y) => {
              if (year === y){
                found = true;
                series[objectSeries[i]].years[y]++;
              }
            });
          }else{
            series[objectSeries[i]].years = {};
          }
          if(!found){
            series[objectSeries[i]].years[year] = 1;
          }
          sortedInvoices[y].orderStatus = sortedInvoices[y].type;
          sortedInvoices[y].number = String(series[objectSeries[i]].id) + "/" + String(series[objectSeries[i]].years[year]).padStart(4, "0");
          yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${sortedInvoices[y].key}`, {
            ...omit(sortedInvoices[y], ['key'])
          });
          yield setPaymentInvoice(sortedInvoices[y]);
          yield put({type: actionSerie.SAVE_INTO_FIRESTORE, payload: { data: { key: objectSeries[i], ...series[objectSeries[i]] }, actionName: 'update' }});
        }
      }
    }
    yield put({ type: actions.UPDATE_INVOICES_BULK_SUCCESS, payload: {}});
  } catch (error) {
    console.log(error);
  }
  // Refresh with pagination after bulk update
  yield put({ type: actions.GET_INVOICES_PAGINATED, payload: { filter, page: 1, pageSize: 50 }});
}

export function* fixPaymentsInvoicesBulk(){
  let data = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME, []);
  const dataSource = [];
  Object.keys(data).map((item, index) => {
    return dataSource.push({
      ...data[item],
      key: item,
    });
  });
  for (let i = 0; i < dataSource.length; i++){
    if (dataSource[i].id && (dataSource[i].invoice_serie !=='7' && dataSource[i].invoice_serie !=='8')){
      let filters = [{field: 'invoice', operator: '==', value : dataSource[i].id}];
      let data = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + 'payments', filters);
      if (data && Object.keys(data).length !== 0){
        let paymentId = Object.keys(data);
        yield call(rsf.firestore.setDocument, getAccountIdPrefix() + `payments/${paymentId[0]}`, {
          ...data[paymentId[0]],
          type: dataSource[i].type,
        });
        console.log('updated payment '+paymentId[0])
      }
    }
  }
  console.log('end');
}

export function* updateDataUserInvoice({payload}) {
  console.log(payload);
  let filters = [];
  filters.push({field: 'billToId', operator: '==', value : payload.dataUser.id});
  filters.push({field: 'orderStatus', operator: '==', value : 'draft'});
  let data = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix()+ COLLECTION_NAME, filters);
  const dataSource = [];
  for (const id in data) {
      if (Object.prototype.hasOwnProperty.call(data, id)) {
        //I MODIFY THE INVOICE VALUES
        const item = data[id];
        item.billTo = payload.dataUser.business_name;
        item.cifTo = payload.dataUser.cif;
        item.billToAddress = payload.dataUser.address + " - " + payload.dataUser.address;
        item.companyTo = payload.dataUser.business_name;
        dataSource.push({
          ...item,
          key: id,
        });
      }
   }
  ///////////////////
  const fieldsToOmit = ['key'];
  for (let i = 0; i < dataSource.length; i++){
    yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${dataSource[i].key}`, {
      ...omit(dataSource[i], fieldsToOmit),
    });
  }
  return yield false;
}

export function* updateInvoiceItemBooking({payload}) {
  const { propertyId, bookingId, items } = payload;

  const setNewInvoice = (item, client, property ,bookingId) => {
    let itemDate = new Date(item.createdAt);
    let itemType = (item.invoice) ? 'invoice': 'note';
    let invoice = clone(newInvoice);
    const newId = generateSystemId();
    invoice.id = newId;
    invoice.number = `#${newId}`;
    invoice.orderStatus = 'draft';
    invoice.orderDate = DateFormatter.getLastDayMonthValue(itemDate);
    invoice.orderMonth = itemDate.getMonth();
    invoice.billTo = client.business_name;
    invoice.billToId = property[0].ownerId;
    invoice.ownerId = property[0].ownerId;
    invoice.billToAddress = `${client.address} ${client.city ? "- " + client.city : ""} ${client.state ? "- " + client.state : ""}`;
    invoice.cifTo = client.cif;
    invoice.companyTo = client.business_name;
    invoice.invoice_serie = (itemType === 'invoice') ? (client.invoice_serie).toString() : noteSeries.normal.id.toString();
    invoice.invoiceProperty = property[0].id;
    invoice.propertyName = property[0].name;
    invoice.invoiceList.pop();
    invoice.vatRate = (item.invoice) ? 21 : 0;
    invoice.type = itemType;
    invoice.invoiceList = getItems(invoice.invoiceList, item, property, bookingId);
    return updateValues(invoice);
  }

  const updateInvoice = (item, invoice, property, bookingId) => {
    invoice.invoiceList = getItems(invoice.invoiceList, item, property, bookingId);
    return updateValues(invoice);
  }

  function* getPropertyFromBooking (propertyId) {
    let filtersProp = [];
    filtersProp.push({ field: 'id', operator: '==', value : propertyId });
    let dataProperty = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME_PROP, filtersProp);
    let property = Object.keys(dataProperty).map((i, index) => {
      return {
        ...dataProperty[i],
        key: i,
      };
    });
    return yield property;
  }

  function* getClientFromProperty (property) {
    let filtersClient = [];
    filtersClient.push({ field: 'id', operator: '==', value : property[0].ownerId });
    let dataClient = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME_CLIENT, filtersClient);
    let client = Object.keys(dataClient).map((i, index) => {
      return {
        ...dataClient[i],
        key: i,
      };
    });
    return yield client;
  }

  function* getInvoiceFromBookingItem (item, property, client) {
    let filters = [];
    let itemDate = new Date(item.createdAt);
    let itemType = (item.invoice) ? 'invoice': 'note';
    let invoiceSerie = (itemType === 'invoice') ? (client.invoice_serie).toString() : noteSeries.normal.id.toString();
    // Use the first and last day of the item's month to isolate by year + month
    // (orderMonth alone matches the same calendar month across all years)
    const firstDayOfMonth = moment(new Date(itemDate.getFullYear(), itemDate.getMonth(), 1, 0, 0)).valueOf();
    const lastDayOfMonth = DateFormatter.getLastDayMonthValue(itemDate);
    filters.push({field: 'orderDate', operator: '>=', value : firstDayOfMonth});
    filters.push({field: 'orderDate', operator: '<=', value : lastDayOfMonth});
    filters.push({field: 'orderMonth', operator: '==', value : itemDate.getMonth()});
    filters.push({field: 'billToId', operator: '==', value : property[0].ownerId });
    filters.push({field: 'type', operator: '==', value : itemType });
    filters.push({field: 'invoiceProperty', operator: '==', value : property[0].id });
    filters.push({field: 'invoice_serie', operator: '==', value : invoiceSerie });
    filters.push({field: 'orderStatus', operator: '==', value : 'draft' });
    filters.push({field: 'deleted_at', operator: '==', value : null });
    return yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME, filters);
  }

  // Helper function to find all invoices that contain items with a specific bookingId
  function* getInvoicesFromBookingId(bookingId, property, client) {
    let filters = [];
    filters.push({field: 'billToId', operator: '==', value : property[0].ownerId });
    filters.push({field: 'invoiceProperty', operator: '==', value : property[0].id });
    filters.push({field: 'orderStatus', operator: '==', value : 'draft' });
    filters.push({field: 'deleted_at', operator: '==', value : null });
    let allInvoices = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME, filters);
    
    // Filter invoices that contain items from this booking
    let invoicesWithBookingItems = {};
    Object.keys(allInvoices).forEach(invoiceKey => {
      const invoice = allInvoices[invoiceKey];
      const hasBookingItems = invoice.invoiceList && invoice.invoiceList.some(item => item.bookingId === bookingId);
      if (hasBookingItems) {
        invoicesWithBookingItems[invoiceKey] = invoice;
      }
    });
    
    return invoicesWithBookingItems;
  }

  const getItems = (itemList , item, property, bookingId) => {
    let cont = 1;
    let itemsAux = itemList
    .filter((i, index) => {
      if ((i.itemId === item.itemId )){
        return false;
      }
      return true;
    })
    .map( (i, index) => {
      i.key = cont;
      cont++;
      return i;
    });

    if (!item.deleted) {
      itemsAux.push({
        key: cont,
        itemName: item.itemName,
        itemType: 'T',
        noTaxable: (item.invoice) ? false : true,
        costs: item.costs,
        qty: item.qty,
        price: item.price,
        propertyId: property[0].id,
        bookingId: bookingId,
        itemId: item.itemId,
        createdAt: item.createdAt
      });
    }
    return itemsAux.sort((a, b) => {
      return a.createdAt - b.createdAt;
    }).map( (i, index) => {
      i.key = index+1;
      return i;
    });
  }

  // Helper: load all finalized (non-draft, non-deleted) invoices for this property/owner
  // Used to detect if a booking item is already billed and prevent duplicate drafts
  function* getFinalizedInvoicesForProperty(property) {
    let filters = [];
    filters.push({field: 'billToId', operator: '==', value : property[0].ownerId });
    filters.push({field: 'invoiceProperty', operator: '==', value : property[0].id });
    filters.push({field: 'deleted_at', operator: '==', value : null });
    const all = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME, filters);
    const finalized = {};
    Object.keys(all).forEach(key => {
      if (all[key].orderStatus !== 'draft') finalized[key] = all[key];
    });
    return finalized;
  }

  // Pure check: is this booking item already present in a finalized invoice?
  const isItemAlreadyFinalized = (item, bookingId, finalizedInvoices) =>
    Object.values(finalizedInvoices).some(inv =>
      inv.invoiceList && inv.invoiceList.some(
        line => line.itemId === item.itemId && line.bookingId === bookingId
      )
    );

  let property = yield getPropertyFromBooking(propertyId);
  let client = yield getClientFromProperty(property);

  try {
    // STEP 1: First, clean up deleted items from all invoices related to this booking
    // This handles the case when items are removed or the booking date changes
    let existingInvoices = yield getInvoicesFromBookingId(bookingId, property, client[0]);
    const deletedItems = items.filter(item => item.deleted === true);
    
    for (const invoiceKey in existingInvoices) {
      if (Object.prototype.hasOwnProperty.call(existingInvoices, invoiceKey)) {
        const invoice = existingInvoices[invoiceKey];
        let hasChanges = false;
        
        // Remove deleted items from this invoice
        deletedItems.forEach(deletedItem => {
          const initialLength = invoice.invoiceList.length;
          invoice.invoiceList = invoice.invoiceList.filter(invItem => !(invItem.itemId === deletedItem.itemId && invItem.bookingId === bookingId));
          if (initialLength !== invoice.invoiceList.length) {
            hasChanges = true;
          }
        });
        
        // If invoice is now empty, mark it as deleted
        if (invoice.invoiceList.length === 0) {
          yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoiceKey}`, {
            deleted_at: new Date().getTime(),
          });
          yield put({ type: actionsLog.SAVE_INTO_FIRESTORE, payload: { data: {
            id: generateSystemId(),
            user_id: null,
            text: 'Draft invoice #'+invoice.id+' deleted: no items remaining from booking',
            description: 'Draft cleanup',
            module: 'booking',
            type: 'info',
            checked: false,
            created_at: new Date().getTime(),
            deleted_at: null,
          }}});
        } else if (hasChanges) {
          // Update invoice with recalculated totals
          const updatedInvoice = updateValues(invoice);
          yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoiceKey}`, {
            ...omit(updatedInvoice, ['key']),
          });
        }
      }
    }

    // STEP 2: Process new/updated items and create or update invoices
    // Load finalized invoices ONCE — used to skip items already billed
    const finalizedInvoices = yield getFinalizedInvoicesForProperty(property);
    const activeItems = items.filter(item => item.deleted !== true);

    // Session-local draft map: prevents race conditions within the loop.
    // When a booking has multiple items in the same month (e.g. arrival + departure + cleaning),
    // the loop runs sequentially. Without this map, each iteration would call
    // getInvoiceFromBookingItem and NOT see the draft created by the previous iteration
    // (because the prior `put`-based insert was async and not awaited).
    // With this map, the second+ items reuse the in-memory draft instead of querying Firestore,
    // ensuring they ALL end up in the SAME grouped draft instead of creating one draft per item.
    // Key format: billToId_propertyId_year_month_type_serie
    const sessionDraftMap = {};

    const getDraftKey = (item) => {
      const d = new Date(item.createdAt);
      const t = item.invoice ? 'invoice' : 'note';
      const s = item.invoice ? client[0].invoice_serie.toString() : noteSeries.normal.id.toString();
      return `${property[0].ownerId}_${property[0].id}_${d.getFullYear()}_${d.getMonth()}_${t}_${s}`;
    };

    for (let i = 0; i < activeItems.length; i++ ){
      let item = activeItems[i];

      // Skip items already present in a finalized invoice — prevents duplicate billing
      if (isItemAlreadyFinalized(item, bookingId, finalizedInvoices)) {
        continue;
      }

      const draftKey = getDraftKey(item);
      let existingDraft = null;
      let existingDocKey = null;

      if (sessionDraftMap[draftKey]) {
        // Reuse in-memory draft — avoids race condition on same-month items
        existingDraft = sessionDraftMap[draftKey].invoiceObj;
        existingDocKey = sessionDraftMap[draftKey].docKey;
      } else {
        // First time we see this month/type combination: query Firestore
        const invoicesFromDB = yield getInvoiceFromBookingItem(item, property, client[0]);
        const keys = Object.keys(invoicesFromDB);
        if (keys.length > 0) {
          existingDraft = invoicesFromDB[keys[0]];
          existingDocKey = keys[0];
        }
      }

      if (!existingDraft) {
        // No draft exists — create one with a direct call (awaited) so the next
        // loop iteration can see it via sessionDraftMap without hitting Firestore again
        let newInv = setNewInvoice(item, client[0], property, bookingId);
        const docRef = yield call(rsf.firestore.addDocument, getAccountIdPrefix() + COLLECTION_NAME, omit(newInv, ['key']));
        newInv.key = docRef.id;
        sessionDraftMap[draftKey] = { invoiceObj: newInv, docKey: docRef.id };
        yield setPaymentInvoice(newInv);
        yield put({ type: actionsLog.SAVE_INTO_FIRESTORE, payload: { data: {
              id: generateSystemId(),
              user_id: null,
              text: 'item '+item.itemName+' has created a new invoice #'+newInv.id,
              description: 'New Invoice from booking',
              module: 'booking',
              type: 'success',
              checked: false,
              created_at: new Date().getTime(),
              deleted_at: null,
              }}});
      } else {
        // Draft exists — add the item to it and write synchronously
        let invoice = updateInvoice(item, { ...existingDraft, key: existingDocKey }, property, bookingId);
        invoice.key = existingDocKey;
        sessionDraftMap[draftKey] = { invoiceObj: invoice, docKey: existingDocKey };
        yield setPaymentInvoice(invoice);
        if (invoice.totalCost === 0) {
          yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoice.key}`, {
            deleted_at: new Date().getTime(),
          });
          sessionDraftMap[draftKey] = null; // draft is gone
        } else {
          yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoice.key}`, {
            ...omit(invoice, ['key']),
          });
        }
      }
    }
  } catch(error){
      console.log(error);
  }
  yield put({ type: actions.UPDATE_INVOICE_ITEM_BOOKING_SUCCESS, payload: {}});
  return true;
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.GET_INVOICE, getInvoice),
    takeLatest(actions.GET_INVOICES, getInvoices),
    takeLatest(actions.GET_INVOICES_PAGINATED, getInvoicesPaginated),
    takeEvery(actions.UPDATE_INVOICE, updateInvoiceSaga),
    takeEvery(actions.UPDATE_INVOICE_ITEM_BOOKING, updateInvoiceItemBooking),
    takeEvery(actions.UPDATE_INVOICES_BULK, updateInvoicesBulk),
    takeEvery(actions.FIX_PAYMENTS_INVOICES_BULK, fixPaymentsInvoicesBulk),
    takeEvery(actions.UPDATE_DATA_USER_INVOICE, updateDataUserInvoice),
    //takeEvery(actions.FIX_PAYMENTS_INVOICE, deleteUpdatePaymentFix)
  ]);
}
